import { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { Breadcrumbs } from "~/components/Breadcrumbs";
import { Navbar } from "~/components/Navbar";
import { SidebarLayout } from "~/components/SidebarLayout";
import { SOCIAL_OG_IMAGE_URL } from "~/consts";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import {
    ReferenceDataIndividualSEO,
    getReferenceDataPageDescription,
    getReferenceDataPageTitle,
} from "~/modules/catalog/seo/ReferenceDataIndividualSEO";
import { ReferenceDataIndividualPage } from "~/modules/catalog/pages/reference-individual";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { Routes, getReferenceDataURL } from "~/routes";
import {
    NEXTJS_MISS_REVALIDATE_TIMEOUT,
    NEXTJS_REVALIDATE_TIMEOUT,
} from "~/server/consts";
import { queryReferenceData } from "~/modules/reference_data/api/router";
import { FilterOperators } from "~/modules/filters/types";
import { ReferenceDataColumns } from "~/modules/reference_data/types";
import type { ReferenceData } from "~/modules/reference_data/types";
import { queryMeasures } from "~/modules/measures/api/router";
import { MeasureColumns, type Measure } from "~/modules/measures/types";
import { DEFAULT_MEASURE_LIST_QUERY } from "~/modules/measures/consts";
import { DEFAULT_REFERENCE_DATA_LIST_QUERY } from "~/modules/reference_data/consts";
import { getTotalAssetCount } from "~/modules/assets/api/getAssetCount";
import { CatalogIndividualLayout } from "~/modules/catalog/components/CatalogIndividualLayout";

interface Props {
    referenceData: ReferenceData;
    measures: Measure[];
    allReferenceData: ReferenceData[];
    totalAssetCount: number;
}

export default function ReferenceDataDetailPage(props: Props) {
    const { referenceData, measures, allReferenceData, totalAssetCount } =
        props;

    const pageUrl = addBaseUrl(
        getReferenceDataURL({ referenceSlug: referenceData.slug }),
    );
    const title = getReferenceDataPageTitle({ referenceData });
    const description = getReferenceDataPageDescription({ referenceData });

    return (
        <PageTrack route={Routes.referenceDataIndividual}>
            <NextSeo
                title={title}
                description={description}
                canonical={pageUrl}
                openGraph={{
                    type: "website",
                    url: pageUrl,
                    title,
                    site_name: "RWA.xyz",
                    description,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content: [
                            referenceData.name,
                            "RWA",
                            "real world assets",
                            "tokenized assets",
                            "data catalog",
                            "reference data",
                            referenceData.category,
                        ]
                            .filter(Boolean)
                            .join(", "),
                    },
                ]}
            />
            <ReferenceDataIndividualSEO referenceData={referenceData} />
            <BreadcrumbSEO
                items={[
                    {
                        name: "Data Catalog",
                        href: Routes.catalog,
                    },
                    {
                        name: referenceData.name,
                        href: getReferenceDataURL({
                            referenceSlug: referenceData.slug,
                        }),
                    },
                ]}
            />
            <UserFetcher>
                {({ user }) => (
                    <Navbar
                        route={Routes.referenceDataIndividual}
                        user={user}
                    />
                )}
            </UserFetcher>
            {/* NOTE - we don't want to show the footer on this page because it messes with the layout */}
            <SidebarLayout pathname={Routes.catalog} showFooter={false}>
                <Breadcrumbs
                    pages={[
                        {
                            name: "Data Catalog",
                            href: Routes.catalog,
                        },
                        {
                            name: "Reference Data",
                        },
                        {
                            name: referenceData.name,
                            current: true,
                        },
                    ]}
                />
                <CatalogIndividualLayout
                    measures={measures}
                    referenceData={allReferenceData}
                    title={referenceData.name}
                    icon={referenceData.icon}
                    color={referenceData.color}
                    shortDescription={referenceData.short_description}
                    description={referenceData.description}
                >
                    <ReferenceDataIndividualPage
                        referenceData={referenceData}
                        totalAssetCount={totalAssetCount}
                    />
                </CatalogIndividualLayout>
            </SidebarLayout>
        </PageTrack>
    );
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const allReferenceRes = await queryReferenceData({
            query: DEFAULT_REFERENCE_DATA_LIST_QUERY,
        });
        const items: ReferenceData[] = allReferenceRes.results;

        if (!items || items.length === 0) {
            console.warn("No reference data found or empty response received");
            return {
                paths: [],
                fallback: "blocking",
            };
        }

        const paths = items.map((item) => ({
            params: {
                referenceSlug: item.slug,
            },
        }));

        return {
            paths,
            fallback: "blocking",
        };
    } catch (error) {
        console.error(
            "Failed to generate static paths for reference data:",
            error,
        );
        return {
            paths: [],
            fallback: "blocking",
        };
    }
};

export const getStaticProps: GetStaticProps<Props> = async (ctx) => {
    const referenceSlug = ctx.params?.referenceSlug as string;

    if (!referenceSlug) {
        return { notFound: true, revalidate: NEXTJS_MISS_REVALIDATE_TIMEOUT };
    }

    const [response, measuresRes, allReferenceRes, totalAssetCountRes] =
        await Promise.all([
            queryReferenceData({
                query: {
                    filter: {
                        field: ReferenceDataColumns.slug,
                        operator: FilterOperators.Equals,
                        value: referenceSlug,
                    },
                    sort: {
                        field: ReferenceDataColumns.name,
                        direction: "asc",
                    },
                    pagination: {
                        page: 1,
                        perPage: 1,
                    },
                },
            }),
            queryMeasures({
                query: {
                    ...DEFAULT_MEASURE_LIST_QUERY,
                    filter: {
                        operator: FilterOperators.And,
                        filters: [
                            {
                                field: MeasureColumns.assetCount,
                                operator: FilterOperators.GreaterThan,
                                value: 0,
                            },
                            {
                                field: MeasureColumns.hidden,
                                operator: FilterOperators.Equals,
                                value: false,
                            },
                        ],
                    },
                },
            }),
            queryReferenceData({
                query: {
                    ...DEFAULT_REFERENCE_DATA_LIST_QUERY,
                    filter: {
                        operator: FilterOperators.And,
                        filters: [
                            {
                                field: ReferenceDataColumns.assetCount,
                                operator: FilterOperators.GreaterThan,
                                value: 0,
                            },
                            {
                                field: ReferenceDataColumns.hidden,
                                operator: FilterOperators.Equals,
                                value: false,
                            },
                        ],
                    },
                },
            }),
            getTotalAssetCount(),
        ]);

    const item = response.results[0];

    if (!item) {
        console.warn(
            `Reference data not found for slug "${referenceSlug}". Redirecting to catalog.`,
        );
        return {
            redirect: {
                permanent: false,
                destination: Routes.catalog,
            },
            revalidate: NEXTJS_MISS_REVALIDATE_TIMEOUT,
        };
    }

    const measures = (measuresRes.results || []).filter(
        (m) => m.asset_count > 0,
    );
    const allReferenceData = allReferenceRes.results || [];

    return {
        props: {
            referenceData: item,
            measures,
            allReferenceData,
            totalAssetCount: totalAssetCountRes.count,
        },
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
};
